import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useModal } from 'react-modal-hook'
import { writeStorage, useLocalStorage } from '@rehooks/local-storage'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import '../../styles/Auth.css'
import AuthService from '../../services/auth.service'
import 'react-simple-keyboard/build/css/index.css'
import KeyboardModal from '../../components/Modal/KeyboardModal'
import { LoginSchema } from '../../schemas/auth'
import InputError from '../../components/InputError/InputError'
import 'react-toastify/dist/ReactToastify.css'
import { AUTH_KEYS, KEYBOARD_DIALOG } from '../../utils/translation-constants'
import NeoInput from '../../components/NeoInput/NeoInput'
import NeoButton from '../../components/NeoButton/NeoButton'
import AuthLayout from './auth-layout'
import Loading from 'custom/assets/loaders/Loading'
import LoginMobile from 'custom/assets/landing/login-mobile.svg'
import BtnAppleStore from 'custom/assets/landing/btnAppleStore.svg'
import BtnGooglePlay from 'custom/assets/landing/btnGooglePlay.svg'
import { useDispatch, useSelector } from 'react-redux'
import { setMenuList } from '../../actions'
import { getInvoiceNoSelector } from '../../selectors'
import menuData from 'custom/config/feature.config.json'
import envConfig from "custom/config/environment"

export default function Login() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [loginRetries] = useLocalStorage('loginRetries')
  const [captcha, setCaptcha] = useState(true)
  const [password, setPassword] = useState('')
  const [username, setUserName] = useState('')
  const [focusVal, setFocusVal] = useState('')
  const setFormikFieldValue = useRef(null)
  const [clearKeyboard, setClearKeyboard] = useState(false)
  const [loading, setLoading] = useState(false)
  const invoiceNo = useSelector(getInvoiceNoSelector)

  const [showKeyboardModal, hideKeyboardModal] = useModal(
    () => (
      <KeyboardModal
        setText={setPasswordFieldValue}
        clearText={clearKeyboard}
        closeModel={hideKeyboardModal}>
        {focusVal === 'password' ? (
          <input className='form-control' disabled value={password} type='text' />
        ) : (
          <input className='form-control' disabled value={username} type='text' />
        )}
        <div className='row d-flex'>
          <button
            type='button'
            className='mt-4 col-4 my-3 mx-auto btn neo-btn-new-design new-design'
            mode='newDesign'
            onClick={hideKeyboardModal}>
            {t(KEYBOARD_DIALOG.SUBMIT)}
          </button>
          <button
            type='button'
            className='mt-4 col-4 my-3 mx-auto btn btn-sm btn-link small new-design-warning-text-color font-weight-600'
            onClick={() => {
              setClearKeyboard(true)
              setPasswordFieldValue('')
            }}>
            {t(KEYBOARD_DIALOG.CLEAR)}
          </button>
        </div>
      </KeyboardModal>
    ),
    [password, clearKeyboard, focusVal, username],
  )

  const attemptLogin = useCallback(async (values, { resetForm }) => {
    try {
      setLoading(true)
      dispatch(setMenuList(menuData))
      const response = await AuthService.login(values)
      if (response.status === 200) {
        if (response.data['otpEnable']) {
          setLoading(false)
          history.push('/auth/otp-verify')
          return
        } else {
          const response1 = await AuthService.verifyOTP({ otpNo: '0000' })
          if (response1.status === 200) {
            history.push('/dashboard')
            setLoading(false)
            AuthService.getUserProfile()
            AuthService.getUserProfileImage()
            AuthService.featureCheck()
          }
        }
      }
    } catch (err) {
      resetForm({})
      // writeStorage('loginRetries', isNaN(loginRetries) ? 1 : loginRetries + 1)
      setLoading(false)
    }
  }, [])

  const setPasswordFieldValue = useCallback(
    (value) => {
      if (focusVal === 'password') {
        setPassword(value)
        setFormikFieldValue.current('password', value)
      } else {
        setUserName(value)
        setFormikFieldValue.current('username', value)
      }
      // if (focusVal != '') {
      //   var y = document.getElementById('floatingLabel' + focusVal)
      //   if (y) {
      //     y.classList.add('label-move-up')
      //   }
      // }
    },
    [setFormikFieldValue, focusVal],
  )

  const setFocusItem = useCallback((value) => {
    setFocusVal(value.target.name)
  }, [])

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='45px' height='45px' />
      </div>
    )
  }

  useEffect(() => {
    dispatch(setMenuList(menuData))
  }, [])

  return (
    <AuthLayout>
      <h2 className='app-name'>{t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.APP_NAME)}</h2>
      <div className='text-center'>
        <img
          className=''
          style={{ margin: '0 auto' }}
          src={LoginMobile}
          alt='mobile logo'
          height='100'
        />
      </div>
      <div className='mx-auto' style={{ width: 320 }}>
        <Formik
          initialValues={{ username: '', password: '' }}
          validationSchema={LoginSchema}
          onSubmit={attemptLogin}>
          {({ isSubmitting, errors, values, touched, handleChange, setFieldValue }) => {
            if (setFormikFieldValue.current === null) {
              setFormikFieldValue.current = setFieldValue
            }
            return (
              <Form>
                <NeoInput
                  tabIndex='1'
                  name='username'
                  text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.USERNAME) + '*'}
                  icon='person'
                  mode='icon'
                  disabled={isSubmitting}
                  onFocus={setFocusItem}
                  // onBlur={setBlurItem}
                  value={values.username}
                  onChange={handleChange}
                  placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.USERNAME)}
                />
                <InputError text={errors.username} touched={touched.username} />
                <div className='my-3' />
                <div className='form-group'>
                  <NeoInput
                    tabIndex='2'
                    name='password'
                    text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.PASSWORD) + '*'}
                    icon='lock'
                    mode='sensitiveIcon'
                    onFocus={setFocusItem}
                    // onBlur={setBlurItem}
                    disabled={isSubmitting}
                    placeholder={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.PASSWORD)}
                    onChange={handleChange}
                    value={values.password}
                  />
                  <InputError text={errors.password} touched={touched.password} />
                </div>
                <div className='my-3' />
                <div className='row'>
                  <div className='col-3'>
                    <div
                      
                      className={focusVal == '' ? 'disabled' : 'cursor-pointer'}
                      onClick={showKeyboardModal}>
                      <i className='material-icons h2'>keyboard</i>
                    </div>
                  </div>
                  {menuData['isForgotPswd'] && (
                    <div className='text-right col-9'>
                      <button
                        tabIndex={'3'}
                        type='button'
                        onClick={() => history.push('/forgot-password')}
                        className='btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
                        {t(AUTH_KEYS.FORGOT_PASSWORD_KEYS.RESET_KEYS.TITLE)}
                      </button>
                    </div>
                  )}
                </div>
                <div className='my-3' />
                {loading === false ? (
                  <NeoButton
                    tabIndex='4'
                    type='submit'
                    mode='newDesign'
                    disabled={!captcha}
                    text={t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.LOGIN_KEYS.LOGIN_BUTTON)}
                    styles={{ fontSize: 16, width: '312px' }}
                  />
                ) : null}
                {loading === true ? renderLoading() : null}
              </Form>
            )
          }}
        </Formik>
      </div>

      <div className='text-center my-3'>
        {menuData['isNewCustomer'] && (
          <button
            tabIndex={'5'}
            type='button'
            onClick={() => history.push('/register')}
            className='btn font-weight-bold new-design-warning-text-color'
            style={{ fontSize: 16 }}>
            {t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.SUBTITLE)}
          </button>
        )}
      </div>

      <div className='text-center my-3'>
        {menuData['isSignUp'] && (
          <button
            tabIndex={'6'}
            type='button'
            onClick={() => history.push('/forgot-username')}
            className='btn font-weight-bold new-design-warning-text-color'
            style={{ fontSize: 16 }}>
            {t(AUTH_KEYS.LOGIN_CONTAINER_KEYS.SIGN_UP_KEYS.SUBTITLE_2)}
          </button>
        )}
      </div>

      <div className='d-flex justify-content-center'>
        {menuData['appStore'] && (
          <button type='button' tabIndex='8'  onClick={() => window.open(envConfig.APP_IOS_LINK, '_blank')} className='btn btn-apple-store'>
            <img className='' src={BtnAppleStore} alt='Apple Store button' />
          </button>
        )}
        {menuData['playStore'] && (
          <button type='button' tabIndex='9' onClick={() => window.open(envConfig.APP_ANDROID_LINK, '_blank')} className='btn btn-play-store'>
            <img className='' src={BtnGooglePlay} alt='Play Store button' />
          </button>
        )}
      </div>
    </AuthLayout>
  )
}
